import {
  Box,
  Card,
  Divider,
  Grid,
  Paper,
  Typography
} from '@mui/material'
import ReactMarkdown from 'react-markdown'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '../../../theme'
import { notDefined } from '../../../utils'
import { NoData, TearoomIcon } from '../widgets'

const makeStyles = (palette) => ({
  header: {
    grid: { px: 2, pt: 1, pb: 1, alignItems: 'center', maxWidth: '700px', margin: '0 auto' },
    iconCol: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    contentCol: { display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center' },
    title: { color: palette.headerColor, lineHeight: 1.2 }
  },
  notice: {
    wrapper: { px: 2, pt: 1, pb: 1, maxWidth: '700px', margin: '0 auto' },
    paper: {
      px: 2,
      py: 1.5,
      background: palette.paperColorDim,
      borderLeft: `4px solid ${palette.headerColor}`
    },
    markdown: {
      fontSize: '0.9rem',
      color: palette.textColor,
      '& p': { margin: '0 0 8px 0' },
      '& p:last-child': { margin: 0 }
    }
  },
  list: { px: 1, pt: 1, pb: 2, maxWidth: '700px', margin: '0 auto' },
  section: {
    card: { width: '100%', mb: 1 },
    titleRow: {
      px: 2,
      pt: 1.5,
      pb: 0.5,
      background: palette.headerTransparentThinColor
    },
    titleText: { fontWeight: 700, color: palette.headerColor },
    descriptionMarkdown: {
      fontSize: '0.8rem',
      opacity: 0.8,
      mt: 0.25,
      '& p': { margin: 0 }
    },
    itemRow: {
      px: 2,
      py: 1,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: '44px'
    },
    itemTitle: { fontWeight: 500, flex: 1 },
    itemPrice: {
      fontWeight: 600,
      color: palette.headerColor,
      ml: 2,
      whiteSpace: 'nowrap'
    }
  }
})

const TearoomHeader = ({ styles, palette }) => {
  const { t } = useTranslation()
  return (
    <Paper elevation={2} square>
      <Grid container sx={styles.header.grid}>
        <Grid item xs={3} sx={styles.header.iconCol}>
          <TearoomIcon sx={{ fontSize: 48, color: palette.headerColor }} />
        </Grid>
        <Grid item xs={9} sx={styles.header.contentCol}>
          <Typography variant="subtitle1" fontWeight={700} sx={styles.header.title}>
            {t('tearoom')}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  )
}

const TearoomNotice = ({ notice, styles }) => {
  if (notDefined(notice) || notice === '') return null
  return (
    <Box sx={styles.notice.wrapper}>
      <Paper elevation={1} sx={styles.notice.paper}>
        <Box sx={styles.notice.markdown}>
          <ReactMarkdown>{notice}</ReactMarkdown>
        </Box>
      </Paper>
    </Box>
  )
}

const formatPrice = (price) => {
  if (notDefined(price)) return null
  const { amount, currency } = price
  return `${amount} ${currency}`
}

const SectionCard = ({ section, styles }) => {
  const { title, description, items = [] } = section
  return (
    <Card sx={styles.section.card} data-testid="tearoom-section">
      <Box sx={styles.section.titleRow}>
        <Typography variant="body1" sx={styles.section.titleText}>
          {title}
        </Typography>
        {!notDefined(description) && description !== '' && (
          <Box sx={styles.section.descriptionMarkdown}>
            <ReactMarkdown>{description}</ReactMarkdown>
          </Box>
        )}
      </Box>
      {items.map((item, i) => (
        <Box key={i}>
          {i > 0 && <Divider />}
          <Box sx={styles.section.itemRow}>
            <Typography sx={styles.section.itemTitle}>{item.title}</Typography>
            {!notDefined(item.price) && (
              <Typography sx={styles.section.itemPrice}>
                {formatPrice(item.price)}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Card>
  )
}

export const TearoomMenu = ({ data }) => {
  const { palette } = useThemeMode()
  const styles = makeStyles(palette)

  if (notDefined(data)) {
    return (
      <Box>
        <NoData label="tearoom-no-menu" />
      </Box>
    )
  }

  const { notice, sections = [] } = data

  return (
    <Box>
      <TearoomHeader styles={styles} palette={palette} />
      <TearoomNotice notice={notice} styles={styles} />
      <Box sx={styles.list}>
        {sections.map((section, i) => (
          <SectionCard key={i} section={section} styles={styles} />
        ))}
      </Box>
    </Box>
  )
}
